import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Button,
  CircularProgress,
  LinearProgress,
  Avatar,
  Skeleton,
  Fade,
  Grow,
  useTheme,
  alpha
} from '@mui/material';
import {
  CloudUpload,
  CheckCircle,
  Error as ErrorIcon,
  Refresh,
  PlayArrow,
  Timer,
  TrendingUp,
  Speed,
  Memory,
  Storage,
  Dns,
  RocketLaunch,
  AutoGraph,
  Schedule
} from '@mui/icons-material';
import axios from 'axios';
import ServiceStatus from './ServiceStatus';

const API_BASE_URL = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

// Animated gradient background component
const GradientCard = ({ children, gradient = 'primary', sx = {}, ...props }) => {
  const gradients = {
    primary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    success: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    warning: 'linear-gradient(135deg, #F2994A 0%, #F2C94C 100%)',
    error: 'linear-gradient(135deg, #ED213A 0%, #93291E 100%)',
    info: 'linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)',
    dark: 'linear-gradient(135deg, #232526 0%, #414345 100%)',
  };

  return (
    <Card
      {...props}
      sx={{
        background: gradients[gradient] || gradients.primary,
        color: 'white',
        borderRadius: 4,
        boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        },
        ...sx
      }}
    >
      {children}
    </Card>
  );
};

// Glass morphism card
const GlassCard = ({ children, sx = {}, ...props }) => {
  const theme = useTheme();
  return (
    <Paper
      {...props}
      sx={{
        background: alpha(theme.palette.background.paper, 0.8),
        backdropFilter: 'blur(20px)',
        borderRadius: 4,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
        },
        ...sx
      }}
    >
      {children}
    </Paper>
  );
};

// Stat card component
function StatCard({ title, value, icon, gradient, subtitle, delay = 0, loading }) {
  return (
    <Grow in timeout={500 + delay}>
      <Box>
        <GradientCard gradient={gradient}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="overline"
                  sx={{
                    opacity: 0.9,
                    fontWeight: 600,
                    letterSpacing: 1.5,
                    fontSize: '0.7rem'
                  }}
                >
                  {title}
                </Typography>
                {loading ? (
                  <Skeleton variant="text" width={80} height={50} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                ) : (
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 800,
                      mt: 0.5,
                      mb: 0.5,
                      textShadow: '0 2px 10px rgba(0,0,0,0.2)'
                    }}
                  >
                    {value}
                  </Typography>
                )}
                {subtitle && (
                  <Typography variant="body2" sx={{ opacity: 0.85, fontWeight: 500 }}>
                    {subtitle}
                  </Typography>
                )}
              </Box>
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                {icon}
              </Avatar>
            </Box>
          </CardContent>
        </GradientCard>
      </Box>
    </Grow>
  );
}

// Activity item component
function ActivityItem({ item, index }) {
  const getIcon = () => {
    if (item.type === 'deployment') {
      return item.status === 'success'
        ? <CheckCircle sx={{ color: '#38ef7d' }} />
        : <ErrorIcon sx={{ color: '#ED213A' }} />;
    }
    return <PlayArrow sx={{ color: '#667eea' }} />;
  };

  const getText = () => {
    if (item.type === 'deployment') {
      return `Deployed ${item.name}`;
    }
    return `Server: ${item.name}`;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      // Invalid date, return the string as-is or formatted
      return dateStr;
    }
    const now = new Date();
    const diffMs = now - date;
    
    // If negative or very small, might be timezone issue - show actual time
    if (diffMs < 0 || diffMs > 86400000 * 365) {
      return date.toLocaleString();
    }
    
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <Fade in timeout={300 + index * 100}>
      <ListItem
        sx={{
          py: 1.5,
          px: 2,
          mb: 1,
          borderRadius: 2,
          bgcolor: 'action.hover',
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: 'action.selected',
            transform: 'translateX(4px)',
          }
        }}
      >
        <ListItemIcon sx={{ minWidth: 44 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'transparent' }}>
            {getIcon()}
          </Avatar>
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography variant="body2" fontWeight={600}>
              {getText()}
            </Typography>
          }
          secondary={
            <Typography variant="caption" color="text.secondary">
              {item.triggered_by || 'System'} • {formatTime(item.created_at)}
            </Typography>
          }
        />
        <Chip
          label={item.status}
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: '0.7rem',
            bgcolor: item.status === 'success'
              ? alpha('#38ef7d', 0.15)
              : item.status === 'failed'
                ? alpha('#ED213A', 0.15)
                : alpha('#667eea', 0.15),
            color: item.status === 'success'
              ? '#11998e'
              : item.status === 'failed'
                ? '#ED213A'
                : '#667eea',
          }}
        />
      </ListItem>
    </Fade>
  );
}

export default function Dashboard({ status }) {
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, activityRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/stats/summary?days=30`, { withCredentials: true }),
        axios.get(`${API_BASE_URL}/api/activity/recent?limit=10`, { withCredentials: true })
      ]);

      setStats(statsRes.data.summary);
      setRecentActivity(activityRes.data.activity || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (ms) => {
    if (!ms) return '0s';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
  };

  const successRate = stats?.totalDeployments > 0
    ? Math.round((stats.successfulDeployments / stats.totalDeployments) * 100)
    : 0;

  const runningServers = status?.apiProcesses?.filter(p => p.status === 'online').length || 0;
  const totalServers = status?.apiProcesses?.length || 0;

  return (
    <Box>
      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Total Deployments"
            value={stats?.totalDeployments || 0}
            icon={<RocketLaunch sx={{ fontSize: 28 }} />}
            gradient="primary"
            subtitle={`${successRate}% success rate`}
            delay={0}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Successful"
            value={stats?.successfulDeployments || 0}
            icon={<CheckCircle sx={{ fontSize: 28 }} />}
            gradient="success"
            subtitle="Completed successfully"
            delay={100}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Servers Running"
            value={`${runningServers}/${totalServers}`}
            icon={<Dns sx={{ fontSize: 28 }} />}
            gradient="info"
            subtitle={`${stats?.serverRestarts || 0} restarts`}
            delay={200}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Avg Build Time"
            value={formatDuration(stats?.avgBuildTimeMs)}
            icon={<Speed sx={{ fontSize: 28 }} />}
            gradient="warning"
            subtitle="Per deployment"
            delay={300}
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Service Status */}
      <Fade in timeout={600}>
        <Box sx={{ mb: 4 }}>
          <GlassCard sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                <Storage sx={{ color: 'primary.main' }} />
              </Avatar>
              <Typography variant="h6" fontWeight={700}>
                Infrastructure Services
              </Typography>
            </Box>
            <ServiceStatus />
          </GlassCard>
        </Box>
      </Fade>

      {/* Bottom Grid */}
      <Grid container spacing={3}>
        {/* System Status */}
        <Grid item xs={12} md={5}>
          <Fade in timeout={700}>
            <Box>
              <GlassCard sx={{ p: 3, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1) }}>
                    <AutoGraph sx={{ color: 'success.main' }} />
                  </Avatar>
                  <Typography variant="h6" fontWeight={700}>
                    Deployment Status
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {status?.adminPanel && (
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: status.adminPanel.deployed
                          ? alpha('#38ef7d', 0.1)
                          : alpha(theme.palette.grey[500], 0.1),
                        border: `1px solid ${status.adminPanel.deployed
                          ? alpha('#38ef7d', 0.3)
                          : alpha(theme.palette.grey[500], 0.2)}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          {status.adminPanel.deployed
                            ? <CheckCircle sx={{ color: '#11998e' }} />
                            : <ErrorIcon sx={{ color: 'grey.500' }} />
                          }
                          <Typography fontWeight={600}>Admin Panel</Typography>
                        </Box>
                        <Chip
                          label={status.adminPanel.deployed ? 'Deployed' : 'Not Deployed'}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            bgcolor: status.adminPanel.deployed ? alpha('#38ef7d', 0.2) : 'grey.200',
                            color: status.adminPanel.deployed ? '#11998e' : 'grey.600'
                          }}
                        />
                      </Box>
                    </Box>
                  )}

                  {status?.website && (
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: status.website.deployed
                          ? alpha('#38ef7d', 0.1)
                          : alpha(theme.palette.grey[500], 0.1),
                        border: `1px solid ${status.website.deployed
                          ? alpha('#38ef7d', 0.3)
                          : alpha(theme.palette.grey[500], 0.2)}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          {status.website.deployed
                            ? <CheckCircle sx={{ color: '#11998e' }} />
                            : <ErrorIcon sx={{ color: 'grey.500' }} />
                          }
                          <Typography fontWeight={600}>Website</Typography>
                        </Box>
                        <Chip
                          label={status.website.deployed ? 'Deployed' : 'Not Deployed'}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            bgcolor: status.website.deployed ? alpha('#38ef7d', 0.2) : 'grey.200',
                            color: status.website.deployed ? '#11998e' : 'grey.600'
                          }}
                        />
                      </Box>
                    </Box>
                  )}

                  {/* Success Rate Progress */}
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        Success Rate
                      </Typography>
                      <Typography variant="body2" fontWeight={700} color="success.main">
                        {successRate}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={successRate}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: alpha('#38ef7d', 0.1),
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                          background: 'linear-gradient(90deg, #11998e, #38ef7d)',
                        }
                      }}
                    />
                  </Box>
                </Box>
              </GlassCard>
            </Box>
          </Fade>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={7}>
          <Fade in timeout={800}>
            <Box>
              <GlassCard sx={{ p: 3, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1) }}>
                      <Schedule sx={{ color: 'info.main' }} />
                    </Avatar>
                    <Typography variant="h6" fontWeight={700}>
                      Recent Activity
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    startIcon={<Refresh />}
                    onClick={fetchDashboardData}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 2,
                    }}
                  >
                    Refresh
                  </Button>
                </Box>

                {loading ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {[1, 2, 3, 4].map((i) => (
                      <Skeleton key={i} variant="rounded" height={56} sx={{ borderRadius: 2 }} />
                    ))}
                  </Box>
                ) : recentActivity.length > 0 ? (
                  <List disablePadding sx={{ maxHeight: 350, overflow: 'auto' }}>
                    {recentActivity.map((item, index) => (
                      <ActivityItem key={`${item.type}-${item.id}-${index}`} item={item} index={index} />
                    ))}
                  </List>
                ) : (
                  <Box
                    sx={{
                      py: 6,
                      textAlign: 'center',
                      color: 'text.secondary'
                    }}
                  >
                    <Schedule sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
                    <Typography variant="body1" fontWeight={500}>
                      No recent activity
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Deployments and server events will appear here
                    </Typography>
                  </Box>
                )}
              </GlassCard>
            </Box>
          </Fade>
        </Grid>
      </Grid>
    </Box>
  );
}
